<template>
  <div class="page-container" :class="{ 'dark': isDarkMode }">
    <!-- Header -->
    <CvHeader 
      :is-authenticated="isAuthenticated" 
      :current-user="currentUser"
      @logout="handleLogout"
    >
      <template #actions>
        <button @click="showClearModal" class="btn-clear">
          <!-- Mobile: Show only icon -->
          <svg class="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
          </svg>
          <!-- Desktop: Show text -->
          <span class="hidden sm:inline">Clear All</span>
        </button>
        <!-- Show Save Draft button for all users -->
        <button @click="handleSaveDraft" class="btn-secondary" :disabled="isFormEmpty || isSaving">
          <span v-if="isSaving" class="flex items-center">
            <svg class="inline w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <!-- Mobile: Shorter text -->
            <span class="sm:hidden">Saving...</span>
            <!-- Desktop: Full text -->
            <span class="hidden sm:inline">Saving...</span>
          </span>
          <span v-else class="flex items-center">
            <!-- Mobile: Show only icon -->
            <svg class="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
            </svg>
            <!-- Desktop: Show text -->
            <span class="hidden sm:inline">Save Draft</span>
          </span>
        </button>
        <!-- Show Download CV button for all users -->
        <!-- Authenticated: opens the download options modal (CV only / CV + references) -->
        <!-- Guests: opens the signup download modal -->
        <button
          type="button"
          @click="handleDownload"
          class="btn-primary"
          :disabled="isPdfGenerating || isPreviewGenerating || isFormEmpty"
        >
          <span v-if="isPdfGenerating" class="flex items-center">
            <svg class="inline w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <!-- Mobile: Show only icon -->
            <svg class="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <!-- Desktop: Show text -->
            <span class="hidden sm:inline">Generating...</span>
          </span>
          <span v-else class="flex items-center">
            <!-- Mobile: Show only icon -->
            <svg class="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <!-- Desktop: Show icon + text -->
            <span class="hidden sm:flex sm:items-center">
              <svg class="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Download CV
            </span>
          </span>
        </button>
      </template>
    </CvHeader>

    <!-- Toast Notifications -->
    <Teleport to="body">
      <Transition name="cv-toast">
        <div v-if="successMessage" class="cv-toast cv-toast--success">
          <svg class="cv-toast__icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <span>{{ successMessage }}</span>
        </div>
      </Transition>
      <Transition name="cv-toast">
        <div v-if="downloadError || saveError" class="cv-toast cv-toast--error">
          <svg class="cv-toast__icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <span>{{ downloadError || saveError }}</span>
        </div>
      </Transition>
    </Teleport>

    <!-- Clear All Confirmation Modal -->
    <div v-if="showClearConfirmModal" class="modal-overlay" @click="hideClearModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Clear All Information</h3>
          <button @click="hideClearModal" class="modal-close">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="modal-icon">
            <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <p class="modal-message">
            Are you sure you want to clear all information? This will remove all personal information, education, experience, and activities data.
          </p>
          <p class="modal-warning">
            <strong>This action cannot be undone.</strong>
          </p>
        </div>
        <div class="modal-footer">
          <button @click="hideClearModal" class="btn-cancel">
            Cancel
          </button>
          <button @click="confirmClearAll" class="btn-danger">
            Clear All Data
          </button>
        </div>
      </div>
    </div>

    <!-- Download Modal for non-authenticated users -->
    <DownloadModal
      v-if="!isAuthenticated && showDownloadModal"
      :is-open="showDownloadModal"
      :cv-data="getCvData()"
      :is-pdf-generating="isPdfGenerating"
      @close="hideDownloadModal"
      @signup-success="handleSignupSuccess"
      @download="handleActualDownload"
    />

    <!-- Download options modal for authenticated users (CV only / CV + references) -->
    <CvDownloadOptionsModal
      v-if="isAuthenticated"
      :is-open="showDownloadOptionsModal"
      :cv-id="getCurrentCvId()"
      @close="showDownloadOptionsModal = false"
      @download-cv-only="handleCvOnlyDownload"
      @download-cv-with-references="handleCvWithReferencesDownload"
    />

    <!-- Save Draft Modal for non-authenticated users -->
    <SaveDraftModal
      v-if="!isAuthenticated && showSaveDraftModal"
      :is-open="showSaveDraftModal"
      :cv-data="getCvData()"
      @close="hideSaveDraftModal"
      @save="handleActualSaveDraft"
      @signup-success="handleSignupSuccess"
    />

    <!-- Main Content with Side-by-Side Layout -->
    <main class="cv-creator-main relative">
      <div class="cv-creator-layout">
        <!-- Left Side: Live Preview -->
        <div class="preview-column">
          <div class="preview-panel">
    
            <div class="preview-content">
              <div 
                v-if="isPreviewLoading" 
                class="preview-loading"
              >
                <div class="loading-spinner"></div>
                <p>Generating preview...</p>
              </div>
              
              <div 
                v-else-if="previewError" 
                class="preview-error"
              >
                <svg class="w-8 h-8 text-red-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <p class="text-red-600">{{ previewError }}</p>
                <button @click="refreshPreview" class="btn-retry">Try Again</button>
              </div>
              
              <div
                v-else-if="isFormEmpty"
                class="preview-placeholder"
              >
                <div class="placeholder-icon">
                  <svg class="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
                <h3 class="placeholder-title">Live Preview</h3>
                <p class="placeholder-text">Your CV will appear here as you fill out the form</p>
                <p class="placeholder-subtext">Start by entering your personal information to see the preview</p>
                <div class="placeholder-tip">
                  <div class="tip-content">
                    <div class="tip-text">
                      <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span>Want to see an example?</span>
                    </div>
                    <button @click="loadSampleData" class="btn-sample-inline">Add Sample Data</button>
                  </div>
                </div>
              </div>
              
              <template v-else>
                <!-- Live Preview Mode -->
                <ClientSidePreview
                  v-if="!isPdfPreviewMode"
                  :cv-data="getCvData()"
                  ref="previewContainer"
                />
                
                <!-- PDF Preview Mode -->
                <PdfOnlyPreview
                  v-else
                  :cv-data="getCvData()"
                  :suspended="suspendPreview"
                  ref="previewContainer"
                  @page-count-changed="handlePageCountChange"
                  @generating-change="isPreviewGenerating = $event"
                />
              </template>
            </div>
            <div class="preview-info">
              <div class="preview-info-content">
                <p class="preview-disclaimer">
                  {{ isPdfPreviewMode ? 'PDF Mode: This shows the exact final PDF formatting with correct line & page breaks.' : 'Live Mode: Final formatting may slightly vary. Switch to PDF Preview mode to see final PDF.' }}
                </p>
                <div class="preview-mode-toggle">
                  <span class="toggle-label">Live</span>
                  <label class="toggle-switch">
                    <input 
                      type="checkbox" 
                      v-model="isPdfPreviewMode"
                      @change="handlePreviewModeToggle"
                    >
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-label">PDF</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Side: Form -->
        <div class="form-panel">
          <div class="section-container-form">
            <div class="max-w-4xl mx-auto pb-0 mb-2">
              <form @submit.prevent="generateCV" class="cv-form">
                <div class="cv-section-anchor">
                  <PersonalInfoForm
                    :personal-info="personalInfo"
                    @update:personal-info="personalInfo = $event"
                    @field-changed="debouncedUpdatePreview"
                  />
                </div>
                <div class="cv-section-anchor">
                  <EducationForm
                    :education-list="educationList"
                    :is-authenticated="isAuthenticated"
                    :syncable-ids="syncableEducationIds"
                    @update:education-list="educationList = $event"
                    @field-changed="debouncedUpdatePreview"
                    @scroll-to-new="scrollToNewEntry"
                    @sync-entry="openSyncModal"
                  />
                </div>
                <div class="cv-section-anchor">
                  <ExperienceForm
                    :experience-list="experienceList"
                    :is-authenticated="isAuthenticated"
                    :syncable-ids="syncableExperienceIds"
                    @update:experience-list="experienceList = $event"
                    @field-changed="debouncedUpdatePreview"
                    @scroll-to-new="scrollToNewEntry"
                    @sync-entry="openSyncModal"
                  />
                </div>
                <div class="cv-section-anchor">
                  <ActivitiesForm
                    :activities-list="activitiesList"
                    :is-authenticated="isAuthenticated"
                    :syncable-ids="syncableActivityIds"
                    @update:activities-list="activitiesList = $event"
                    @field-changed="debouncedUpdatePreview"
                    @scroll-to-new="scrollToNewEntry"
                    @sync-entry="openSyncModal"
                  />
                </div>
                <div class="cv-section-anchor">
                  <SkillCategoriesForm
                    :skill-categories="skillCategories"
                    :is-authenticated="isAuthenticated"
                    @update:skill-categories="skillCategories = $event"
                    @field-changed="debouncedUpdatePreview"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>

  <!-- Sync to profile modal -->
  <SyncToProfileModal
    :is-open="syncModalOpen"
    :entry="syncModalEntry"
    :entry-type="syncModalEntryType"
    @close="syncModalOpen = false"
    @synced="handleSynced"
  />
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useCvData } from './composables/useCvData.js'
import { useAuth } from './composables/useAuth.js'
import { useCvActions } from './composables/useCvActions.js'
import { useCvPreview } from './composables/useCvPreview.js'
import { useCvLoader } from './composables/useCvLoader.js'
import { useDarkMode } from './composables/useDarkMode.js'
import { useCvDirectDownload } from './composables/useCvDirectDownload.js'


// Services
import { ApiService } from './services/ApiService.js'
import { CV_CONFIG, getCSRFToken } from './config/cvConfig.js'

// Components
import CvHeader from './components/CvHeader.vue'
import PersonalInfoForm from './components/PersonalInfoForm.vue'
import EducationForm from './components/EducationForm.vue'
import ExperienceForm from './components/ExperienceForm.vue'
import ActivitiesForm from './components/ActivitiesForm.vue'
import SkillCategoriesForm from './components/SkillCategoriesForm.vue'
import ClientSidePreview from './components/ClientSidePreview.vue'
import DownloadModal from './components/DownloadModal.vue'
import SaveDraftModal from './components/SaveDraftModal.vue'
import PdfOnlyPreview from './components/PdfOnlyPreview.vue'
import CvDownloadOptionsModal from './components/CvDownloadOptionsModal.vue'
import SyncToProfileModal from './components/SyncToProfileModal.vue'

// Styles
import './cvCreator.css'

export default {
  name: 'CvCreatorWithPreview',
  components: {
    CvHeader,
    PersonalInfoForm,
    EducationForm,
    ExperienceForm,
    ActivitiesForm,
    SkillCategoriesForm,
    ClientSidePreview,
    DownloadModal,
    SaveDraftModal,
    PdfOnlyPreview,
    CvDownloadOptionsModal,
    SyncToProfileModal
  },
  props: {
    // Accept initial data from wizard or other sources
    initialData: {
      type: Object,
      default: null
    },
    // CV ID to load from backend
    cvId: {
      type: [String, Number],
      default: null
    },
    // Whether to use client-side preview
    useClientPreview: {
      type: Boolean,
      default: true
    },
    // Whether to show modals or handle actions directly
    directMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['cv-downloaded', 'draft-saved', 'data-cleared'],
  setup(props, { emit }) {
    // Composables
    const { isDarkMode } = useDarkMode()
    const { isAuthenticated, currentUser, checkAuthStatus, handleLogout } = useAuth()
    const { 
      personalInfo, 
      educationList, 
      experienceList, 
      activitiesList,
      skillCategories,
      isFormEmpty,
      getCvData,
      getCvDataForBackend,
      setCvData,
      clearCvData
    } = useCvData(props.initialData)
    const {
      isPdfGenerating,
      downloadError,
      saveError,
      saveDraft,
      clearErrors
    } = useCvActions()
    const {
      isLoading: isCvLoading,
      loadError,
      loadCvById
    } = useCvLoader()
    const {
      isPreviewLoading,
      previewError,
      previewHtml,
      generatePreview,
      refreshPreview
    } = useCvPreview()
    const { downloadCvWithReferences } = useCvDirectDownload()

    // Local state
    const showClearConfirmModal = ref(false)
    const showDownloadModal = ref(false)
    const showDownloadOptionsModal = ref(false)
    const showSaveDraftModal = ref(false)
    const successMessage = ref('')
    const previewContainer = ref(null)
    const useClientSidePreview = ref(props.useClientPreview)
    const isSaving = ref(false)
    const isPdfPreviewMode = ref(false)
    const isPreviewGenerating = ref(false)
    const suspendPreview = ref(false)
    
    // Sync-to-profile state
    const syncModalOpen = ref(false)
    const syncModalEntry = ref(null)
    const syncModalEntryType = ref('experience')
    const experienceOriginals = ref({})
    const educationOriginals = ref({})
    const activityOriginals = ref({})

    function stripMeta(entry) {
      const { id, masterExperienceEntryId, masterEducationEntryId, masterActivityId, ...rest } = entry
      return rest
    }

    function captureOriginals() {
      experienceOriginals.value = {}
      educationOriginals.value = {}
      activityOriginals.value = {}
      experienceList.value.forEach(e => { if (e.id) experienceOriginals.value[e.id] = JSON.stringify(stripMeta(e)) })
      educationList.value.forEach(e => { if (e.id) educationOriginals.value[e.id] = JSON.stringify(stripMeta(e)) })
      activitiesList.value.forEach(e => { if (e.id) activityOriginals.value[e.id] = JSON.stringify(stripMeta(e)) })
    }

    const syncableExperienceIds = computed(() =>
      experienceList.value
        .filter(e => {
          if (!e.id || !e.jobTitle || !e.company) return false
          if (!e.masterExperienceEntryId) return true
          const orig = experienceOriginals.value[e.id]
          return orig ? JSON.stringify(stripMeta(e)) !== orig : false
        })
        .map(e => e.id)
    )

    const syncableEducationIds = computed(() =>
      educationList.value
        .filter(e => {
          if (!e.id || !e.institution) return false
          if (!e.masterEducationEntryId) return true
          const orig = educationOriginals.value[e.id]
          return orig ? JSON.stringify(stripMeta(e)) !== orig : false
        })
        .map(e => e.id)
    )

    const syncableActivityIds = computed(() =>
      activitiesList.value
        .filter(e => {
          if (!e.id || !e.organization) return false
          if (!e.masterActivityId) return true
          const orig = activityOriginals.value[e.id]
          return orig ? JSON.stringify(stripMeta(e)) !== orig : false
        })
        .map(e => e.id)
    )

    function openSyncModal({ entry, entryType }) {
      syncModalEntry.value = entry
      syncModalEntryType.value = entryType
      syncModalOpen.value = true
    }

    function handleSynced({ entry, entryType }) {
      if (entry.id) {
        if (entryType === 'experience') experienceOriginals.value[entry.id] = JSON.stringify(stripMeta(entry))
        else if (entryType === 'education') educationOriginals.value[entry.id] = JSON.stringify(stripMeta(entry))
        else if (entryType === 'activity') activityOriginals.value[entry.id] = JSON.stringify(stripMeta(entry))
      }
      syncModalOpen.value = false
    }

    // Auto-save configuration for non-authenticated users using centralized config
    const AUTO_SAVE_INTERVAL = CV_CONFIG.autoSave.interval
    const EXPIRY_TIME = CV_CONFIG.autoSave.expiryTime
    const STORAGE_KEY = CV_CONFIG.autoSave.storageKey
    let autoSaveTimer = null

    // Get configuration from window object (set by Rails view)
    const getConfigFromWindow = () => {
      if (typeof window !== 'undefined' && window.cvCreatorConfig) {
        return {
          cvId: window.cvCreatorConfig.cvId || props.cvId,
          showSample: window.cvCreatorConfig.showSample || false,
          wizardMode: window.cvCreatorConfig.wizardMode || false,
          mode: window.cvCreatorConfig.mode || 'new'
        }
      }
      return {
        cvId: props.cvId,
        showSample: false,
        wizardMode: false,
        mode: 'new'
      }
    }

    // Initialize component
    onMounted(async () => {
      const config = getConfigFromWindow()
      
      console.log('🚀 CvCreatorWithPreview mounted with config:', {
        ...config,
        initialData: props.initialData,
        useClientPreview: props.useClientPreview,
        directMode: props.directMode
      })
      
      await checkAuthStatus()
      
      // Priority order for data loading:
      // 1. CV ID from config/props (highest priority - from master data creation or edit)
      // 2. Sample data mode
      // 3. Wizard mode with initial data
      // 4. Auto-saved data for non-authenticated users
      
      if (config.cvId) {
        // Load CV data from backend using the provided ID
        console.log('🔄 Loading CV data from backend, ID:', config.cvId)
        const cvData = await loadCvById(config.cvId)
        if (cvData) {
          setCvData(cvData)
          captureOriginals()
          console.log('✅ CV data loaded successfully:', cvData)
        } else if (loadError.value) {
          console.error('❌ Failed to load CV data:', loadError.value)
          successMessage.value = `Error loading CV: ${loadError.value}`
          clearMessages()
        }
      } else if (sessionStorage.getItem('cv_parser_result')) {
        try {
          const stored = JSON.parse(sessionStorage.getItem('cv_parser_result'))
          sessionStorage.removeItem('cv_parser_result')
          const age = Date.now() - (stored.timestamp || 0)
          if (age < 10 * 60 * 1000 && stored.data) {
            setCvData(stored.data)
          }
        } catch {
          sessionStorage.removeItem('cv_parser_result')
        }
      } else if (config.showSample) {
        // Load sample data if requested
        console.log('🎯 Loading sample data')
        loadSampleData()
      } else if (config.wizardMode && props.initialData) {
        // Use wizard data if in wizard mode and data is provided
        setCvData(props.initialData)
        console.log('🧙 Using wizard data:', props.initialData)
      } else if (props.initialData) {
        // Use initial data if provided
        setCvData(props.initialData)
        console.log('📝 Using initial data:', props.initialData)
      } else if (!isAuthenticated.value) {
        // For non-authenticated users, try to load from localStorage
        const loaded = loadFromLocalStorage()
        if (loaded) {
          console.log('💾 Loaded data from localStorage')
        }
        startAutoSave()
      }
      
      // Generate initial preview after data is loaded
      await nextTick()
      console.log('🎨 Generating initial preview with data:', getCvData())
      generatePreview(getCvData())
    })

    onUnmounted(() => {
      localStorage.removeItem(STORAGE_KEY)
      stopAutoSave()
    })

    // Watch for changes in CV ID (if route changes)
    watch(() => {
      const config = getConfigFromWindow()
      return config.cvId
    }, async (newCvId) => {
      if (newCvId) {
        console.log('🔄 CV ID changed, loading new data:', newCvId)
        const cvData = await loadCvById(newCvId)
        if (cvData) {
          setCvData(cvData)
          generatePreview(getCvData())
        }
      }
    })

    // Watch for changes in initial data (if wizard data updates)
    watch(() => props.initialData, (newData) => {
      const config = getConfigFromWindow()
      if (newData && !config.cvId) { // Only use wizard data if no CV ID is provided
        setCvData(newData)
        generatePreview(getCvData())
      }
    }, { deep: true })

    // Auto-save for non-authenticated users
    const saveToLocalStorage = () => {
      if (isAuthenticated.value) return // Don't auto-save for authenticated users
      
      try {
        const cvData = {
          ...getCvData(),
          timestamp: Date.now()
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cvData))
      } catch (error) {
        console.error('Failed to save to localStorage:', error)
      }
    }

    const loadFromLocalStorage = () => {
      try {
        const savedData = localStorage.getItem(STORAGE_KEY)
        if (!savedData) return false

        const parsedData = JSON.parse(savedData)
        const now = Date.now()
        const timeDiff = now - (parsedData.timestamp || 0)

        if (timeDiff > EXPIRY_TIME) {
          localStorage.removeItem(STORAGE_KEY)
          return false
        }

        setCvData(parsedData)
        return true
      } catch (error) {
        console.error('Failed to load from localStorage:', error)
        localStorage.removeItem(STORAGE_KEY)
        return false
      }
    }

    const startAutoSave = () => {
      if (autoSaveTimer) clearInterval(autoSaveTimer)
      
      autoSaveTimer = setInterval(() => {
        if (!isFormEmpty.value) {
          saveToLocalStorage()
        }
      }, AUTO_SAVE_INTERVAL)
    }

    const stopAutoSave = () => {
      if (autoSaveTimer) {
        clearInterval(autoSaveTimer)
        autoSaveTimer = null
      }
    }

    // Clear success/error messages after timeout
    const clearMessages = () => {
      setTimeout(() => {
        successMessage.value = ''
        clearErrors()
      }, 5000)
    }

    // Preview update with debouncing
    let debounceTimer = null
    const debouncedUpdatePreview = () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        generatePreview(getCvData())
        
        // Auto-save for non-authenticated users
        if (!isAuthenticated.value && !isFormEmpty.value) {
          saveToLocalStorage()
        }
      }, 300)
    }

    // Clear modal handlers
    const showClearModal = () => {
      showClearConfirmModal.value = true
    }

    const hideClearModal = () => {
      showClearConfirmModal.value = false
    }

    const confirmClearAll = () => {
      clearCvData()
      hideClearModal()
      successMessage.value = 'All data has been cleared.'
      clearMessages()
      generatePreview(getCvData())
      emit('data-cleared')
    }

    // Download handlers - simplified using services
    const handleDownload = () => {
      if (props.directMode) {
        handleActualDownload()
      } else if (isAuthenticated.value) {
        // Logged-in users choose between CV only and CV + references
        showDownloadOptionsModal.value = true
      } else {
        showDownloadModal.value = true
      }
    }

    // iOS Safari can't use a[download], and opening a tab after async work gets popup-blocked.
    // So we navigate synchronously within the click gesture and let the opened tab fetch the PDF.
    const downloadPdfIOS = () => {
      const cvId = window.cvCreatorConfig?.cvId
      if (cvId) {
        window.open(`/api/cvs/${cvId}/pdf`, '_blank')
        return
      }
      // Guest/unsaved CV: POST the data into a new tab. If the popup is blocked (window.open
      // returns null), fall back to the current tab so the download still happens.
      const tabName = 'cvPdfDownload'
      const newTab = window.open('', tabName)

      const form = document.createElement('form')
      form.method = 'POST'
      form.action = CV_CONFIG.api.generatePdf
      form.target = newTab ? tabName : '_self'
      form.style.display = 'none'

      const payload = document.createElement('input')
      payload.type = 'hidden'
      payload.name = 'payload'
      payload.value = JSON.stringify(getCvData())
      form.appendChild(payload)

      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    }

    const handleActualDownload = async (filename = null) => {
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        downloadPdfIOS()
        if (showDownloadModal.value) hideDownloadModal()
        emit('cv-downloaded', { success: true })
        return
      }

      try {
        isPdfGenerating.value = true

        const userName = personalInfo.value.fullName || 'CV'
        const finalFilename = filename || `${userName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`

        let blob
        if (isPdfPreviewMode.value) {
          // Reuse the preview's PDF to avoid a second concurrent Chrome render (which OOMs the
          // dyno). Pause preview regeneration and wait for any in-flight render to finish first.
          suspendPreview.value = true
          if (isPreviewGenerating.value) {
            await new Promise((resolve) => {
              const stop = watch(isPreviewGenerating, (generating) => {
                if (!generating) { stop(); resolve() }
              })
              setTimeout(() => { stop(); resolve() }, 25000)
            })
          }
          blob = previewContainer.value?.pdfPreviewData?.blob || null
        }

        if (!blob) {
          // Try to save for authenticated users to get a CV ID (non-fatal if it fails)
          let cvId = typeof window !== 'undefined' ? window.cvCreatorConfig?.cvId : null
          if (isAuthenticated.value && !cvId) {
            try {
              const saved = await saveCurrentCvToDatabase('published')
              cvId = saved?.cv?.id
            } catch (saveError) {
              console.warn('Could not save CV before PDF generation, falling back to guest mode:', saveError)
            }
          }

          const endpoint = cvId ? `/api/cvs/${cvId}/pdf` : CV_CONFIG.api.generatePdf
          const fetchOptions = cvId
            ? { method: 'GET', headers: { Accept: 'application/pdf', 'X-CSRF-Token': getCSRFToken() } }
            : {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/pdf', 'X-CSRF-Token': getCSRFToken() },
                body: JSON.stringify(getCvData())
              }

          const response = await fetch(endpoint, fetchOptions)
          if (!response.ok) throw new Error('PDF generation failed')
          blob = await response.blob()
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = finalFilename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 1000)

        emit('cv-downloaded', { success: true, filename: finalFilename })

      } catch (error) {
        console.error('Error generating PDF:', error)
        emit('cv-downloaded', { success: false, error: error.message })
      } finally {
        isPdfGenerating.value = false
        suspendPreview.value = false
        if (showDownloadModal.value) hideDownloadModal()
        clearMessages()
      }
    }

    // Helper function to save CV to database
    const saveCurrentCvToDatabase = async (status = 'draft') => {
      try {
        const cvData = getCvDataForBackend()
        const config = getConfigFromWindow()
        
        const result = await ApiService.saveCv(cvData, config.cvId, status)
        
        // Update config with new CV ID if this was a new CV
        if (!config.cvId && result.cv?.id) {
          if (typeof window !== 'undefined' && window.cvCreatorConfig) {
            window.cvCreatorConfig.cvId = result.cv.id
          }
        }
        
        return result
      } catch (error) {
        console.warn('Failed to save CV to database:', error)
        throw error
      }
    }

    // Save draft handlers - simplified using services
    const handleSaveDraft = async () => {
      if (isAuthenticated.value) {
        await saveAuthenticatedUserDraft()
      } else {
        handleUnauthenticatedUserDraft()
      }
    }

    const saveAuthenticatedUserDraft = async () => {
      isSaving.value = true
      try {
        const result = await saveCurrentCvToDatabase('draft')
        const config = getConfigFromWindow()
        
        successMessage.value = config.cvId ? 'CV updated successfully!' : 'CV saved successfully!'
        emit('draft-saved', { success: true, data: result })
        
      } catch (error) {
        console.error('Save error:', error)
        emit('draft-saved', { success: false, error: error.message })
      } finally {
        isSaving.value = false
        clearMessages()
      }
    }

    const handleUnauthenticatedUserDraft = () => {
      if (props.directMode) {
        handleActualSaveDraft()
      } else {
        showSaveDraftModal.value = true
      }
    }

    const handleActualSaveDraft = async (draftName = null) => {
      try {
        const cvData = getCvDataForBackend()
        const result = await ApiService.saveDraft(cvData, draftName)
        
        successMessage.value = 'Draft saved successfully!'
        hideSaveDraftModal()
        emit('draft-saved', { success: true, data: result })
        
      } catch (error) {
        emit('draft-saved', { success: false, error: error.message })
      } finally {
        clearMessages()
      }
    }

    const hideSaveDraftModal = () => {
      showSaveDraftModal.value = false
    }

    const hideDownloadModal = () => {
      showDownloadModal.value = false
    }

    const handleSignupSuccess = () => {
      checkAuthStatus()
      hideDownloadModal()
      hideSaveDraftModal()
      stopAutoSave()
    }

    // Sample data loader
    const loadSampleData = () => {
      const sampleData = {
        personalInfo: {
          fullName: 'Maximilian Weber',
          email: 'maximilian.weber@email.com',
          phone: '+49 (30) 123-4567',
          location: 'Frankfurt am Main, Germany'
        },
        educationList: [{
          institution: 'London School of Economics',
          location: 'London, United Kingdom',
          startMonth: '9',
          startYear: '2022',
          endMonth: '6',
          endYear: '2024',
          programName: 'Finance',
          programType: 'Master of Science',
          gpa: 'Distinction',
          bulletPoints: [
            'Graduated with Distinction (top 10% of class), Dean\'s List',
            'Coursework: Corporate Finance, Private Equity, Investment Banking, M&A',
            'President of Finance Society; organized career events with 150+ attendees'
          ],
          isCurrent: false
        }, {
          institution: 'Universität Mannheim',
          location: 'Mannheim, Germany',
          startMonth: '9',
          startYear: '2019',
          endMonth: '6',
          endYear: '2022',
          programName: 'Business Administration',
          programType: 'Bachelor of Science',
          gpa: '1.2 (Top 5%)',
          bulletPoints: [
            'Magna Cum Laude (1.2 GPA), top 5% of class',
            'Coursework: Financial Markets, Corporate Finance, Strategic Management',
            'Captain of Rowing Team; led team to German University Championship'
          ],
          isCurrent: false
        }],
        experienceList: [{
          jobTitle: 'Summer Analyst',
          company: 'Goldman Sachs',
          startMonth: '6',
          startYear: '2023',
          endMonth: '8',
          endYear: '2023',
          location: 'Frankfurt am Main, Germany',
          companyDescription: 'Leading global investment banking & management firm',
          bulletPoints: [
            'Executed €1.8B+ M&A transactions across DAX and European mid-market',
            'Built financial models (DCF, LBO, comps) for DACH region transactions',
            'Conducted due diligence on automotive and industrial sectors; identified €200M+ synergies',
            'Prepared pitch materials for executive presentations to DAX 30 companies'
          ],
          isCurrent: false
        }, {
          jobTitle: 'Investment Banking Intern',
          company: 'Deutsche Bank',
          startMonth: '7',
          startYear: '2022',
          endMonth: '9',
          endYear: '2022',
          location: 'Frankfurt am Main, Germany',
          companyDescription: 'Germany\'s leading bank and major global investment bank',
          bulletPoints: [
            'Supported €500M+ IPO for German tech company, coordinating with BaFin',
            'Analyzed debt capital market opportunities across the DACH region',
            'Conducted industry research on Mittelstand companies for strategic mandates',
            'Presented to MDs, securing approval for €50M credit facility expansion'
          ],
          isCurrent: false
        }],
        activitiesList: [{
          organization: 'Finance Forward Magazine',
          role: 'Contributing Author',
          startMonth: '1',
          startYear: '2023',
          endMonth: '12',
          endYear: '2023',
          bulletPoints: [
            'Published 4 articles on German PE trends and EU ESG regulations',
            'Interviewed executives from Rocket Internet, SAP Ventures, and EQT Partners',
            'Recognized as "Rising Voice in German Finance" by Handelsblatt',
          ],
          isCurrent: false
        }, {
          organization: 'Deutsche Stiftung für Talente',
          role: 'Board Member',
          startMonth: '9',
          startYear: '2022',
          endMonth: '6',
          endYear: '2024',
          bulletPoints: [
            'Advised on €25M scholarship fund supporting STEM and business students',
            'Secured €800K in fundraising from DAX companies and family offices',
            'Mentored 15+ students transitioning from German universities to IB',
          ],
          isCurrent: false
        }],
        skillCategories: [{
          name: 'Financial Modeling & Analysis',
          content: 'DCF, LBO, M&A Comps, Credit Analysis, Portfolio Optimization'
        }, {
          name: 'Technical Skills',
          content: 'Advanced Excel, PowerPoint, Bloomberg Terminal, Thomson Reuters Eikon, VBA'
        }, {
          name: 'Languages',
          content: 'German (Native), English (Fluent), French (Conversational)'
        }]
      }
      
      setCvData(sampleData)
      generatePreview(getCvData())
    }

    const scrollToNewEntry = async (payload) => {
      await nextTick()
      
      try {
        let targetElement = null
        if (payload.type === 'education') {
          const educationSection = Array.from(document.querySelectorAll('.form-section h2')).find(h2 => 
            h2.textContent.includes('Education')
          )?.closest('.form-section')
          if (educationSection) {
            targetElement = educationSection.querySelectorAll('.form-card')[payload.index]
          }
        } else if (payload.type === 'experience') {
          const experienceSection = Array.from(document.querySelectorAll('.form-section h2')).find(h2 => 
            h2.textContent.includes('Professional Experience')
          )?.closest('.form-section')
          if (experienceSection) {
            targetElement = experienceSection.querySelectorAll('.form-card')[payload.index]
          }
        } else if (payload.type === 'activity') {
          const activitySection = Array.from(document.querySelectorAll('.form-section h2')).find(h2 => 
            h2.textContent.includes('Extracurricular Activities')
          )?.closest('.form-section')
          if (activitySection) {
            targetElement = activitySection.querySelectorAll('.form-card')[payload.index]
          }
        }
        
        if (targetElement) {
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          })
          
          // Brief highlight effect using config
          targetElement.style.transition = 'box-shadow 0.3s ease'
          targetElement.style.boxShadow = '0 0 0 3px rgba(0, 100, 220, 0.4)';
          setTimeout(() => {
            if (targetElement.style) {
              targetElement.style.boxShadow = ''
            }
          }, CV_CONFIG.ui.highlightDuration)
        }
      } catch (error) {
        console.error('Error scrolling to new entry:', error)
      }
    }

    // Handle preview mode toggle
    const handlePreviewModeToggle = () => {
      console.log('🔄 Preview mode toggled to:', isPdfPreviewMode.value ? 'PDF Preview' : 'Live Preview')
    }

    // Handle page count changes from PDF preview
    const handlePageCountChange = (pageCount) => {
      console.log('📄 PDF Preview page count changed:', pageCount)
    }

    // References checking and download handlers
    const getCurrentCvId = () => {
      const config = getConfigFromWindow()
      return config.cvId
    }

    const handleCvOnlyDownload = async () => {
      await handleActualDownload()
    }

    const handleCvWithReferencesDownload = async () => {
      try {
        isPdfGenerating.value = true

        // References hang off saved CV entries, so make sure the CV is saved first
        let cvId = getCurrentCvId()
        if (!cvId) {
          const saved = await saveCurrentCvToDatabase('published')
          cvId = saved?.cv?.id
        }
        if (!cvId) throw new Error('Could not save the CV before downloading it with references')

        const result = await downloadCvWithReferences(cvId)

        if (result.success) {
          successMessage.value = 'CV with references downloaded successfully!'
          emit('cv-downloaded', { success: true, filename: result.filename, withReferences: true })
        } else if (!result.upgradeRequired) {
          // 402 already opened the global UpgradeModal via the composable
          emit('cv-downloaded', { success: false, error: result.error, withReferences: true })
        }
      } catch (error) {
        console.error('Error downloading CV with references:', error)
        emit('cv-downloaded', { success: false, error: error.message, withReferences: true })
      } finally {
        isPdfGenerating.value = false
        clearMessages()
      }
    }

    // Cleanup
    onMounted(() => {
      return () => {
        stopAutoSave()
        clearTimeout(debounceTimer)
      }
    })

    return {
      // Dark mode
      isDarkMode,
      
      // Auth
      isAuthenticated,
      currentUser,
      handleLogout,
      
      // CV data
      personalInfo,
      educationList,
      experienceList,
      activitiesList,
      skillCategories,
      isFormEmpty,
      getCvData,
      
      // Preview
      isPreviewLoading,
      previewError,
      previewHtml,
      previewContainer,
      useClientSidePreview,
      debouncedUpdatePreview,
      refreshPreview,
      isPdfPreviewMode,
      isPreviewGenerating,
      suspendPreview,
      handlePreviewModeToggle,
      handlePageCountChange,
      
      // Loading
      isSaving,
      
      // Actions
      isPdfGenerating,
      downloadError,
      saveError,
      successMessage,
      
      // Modal states
      showClearConfirmModal,
      showDownloadModal,
      showDownloadOptionsModal,
      showSaveDraftModal,
      
      // Handlers
      showClearModal,
      hideClearModal,
      confirmClearAll,
      handleDownload,
      handleActualDownload,
      hideDownloadModal,
      handleSignupSuccess,
      handleSaveDraft,
      handleActualSaveDraft,
      hideSaveDraftModal,
      loadSampleData,
      scrollToNewEntry,
      
      // References
      getCurrentCvId,
      handleCvOnlyDownload,
      handleCvWithReferencesDownload,

      // Sync to profile
      syncModalOpen,
      syncModalEntry,
      syncModalEntryType,
      syncableExperienceIds,
      syncableEducationIds,
      syncableActivityIds,
      openSyncModal,
      handleSynced
    }
  }
}
</script>

<style scoped>
/* Styles are imported from cvCreator.css */
</style>
